<template>
  <section v-if="visible" class="health-link" :class="{ 'health-link--connected': linked }" :aria-label="'Gesundheitsdaten aus ' + (linked ? linkedLabel : importLabel)">
    <div class="health-link__head">
      <span class="health-link__icon"><v-icon color="#ff6481" size="23">mdi-heart</v-icon></span>
      <div class="health-link__heading">
        <span class="health-link__eyebrow">{{ linked ? linkedLabel : importLabel }}</span>
        <h3>{{ linked ? 'Dein Training. Deine Daten.' : 'Gesundheitsdaten hinzufügen' }}</h3>
      </div>
      <v-icon v-if="linked" color="primary" size="23">mdi-check-decagram</v-icon>
    </div>
    <template v-if="linked">
      <template v-if="hasMeasurements">
        <p class="health-link__source">{{ sourceName(linked) }} · {{ linked.activityName }}</p>
        <p class="health-link__time">{{ dateTime(linked.startedAt) }} – {{ time(linked.endedAt) }}</p>
        <div class="health-link__metrics">
          <div><strong>{{ duration(linked.durationSeconds) }}</strong><span>Aufgezeichnete Dauer</span></div>
          <div v-if="hasNumber(linked.activeEnergyKilocalories)"><strong>{{ rounded(linked.activeEnergyKilocalories) }} <small>kcal</small></strong><span>Aktive Energie</span></div>
          <div v-if="hasNumber(linked.totalEnergyKilocalories)"><strong>{{ rounded(linked.totalEnergyKilocalories) }} <small>kcal</small></strong><span>Gesamtenergie</span></div>
          <div v-if="hasNumber(linked.averageHeartRate)"><strong>{{ rounded(linked.averageHeartRate) }} <small>BPM</small></strong><span>Ø Messwerte</span></div>
          <div v-if="hasNumber(linked.minimumHeartRate)"><strong>{{ rounded(linked.minimumHeartRate) }} <small>BPM</small></strong><span>Niedrigster Puls</span></div>
          <div v-if="hasNumber(linked.maximumHeartRate)"><strong>{{ rounded(linked.maximumHeartRate) }} <small>BPM</small></strong><span>Höchster Puls</span></div>
          <div v-if="hasNumber(linked.distanceMeters) && linked.distanceMeters > 0"><strong>{{ distance(linked.distanceMeters) }}</strong><span>Distanz</span></div>
        </div>
        <div v-if="pulsePlot.dots.length" class="health-link__pulse">
          <span>Pulsmesswerte der Aufzeichnung</span>
          <svg viewBox="0 0 320 76" role="img" aria-label="Verfügbare Pulsmesswerte über die Trainingsdauer; Lücken bleiben offen">
            <line x1="0" y1="72" x2="320" y2="72" stroke="rgba(255,255,255,.08)" />
            <polyline v-for="(path, i) in pulsePlot.paths" :key="'line-' + i" :points="path" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <circle v-for="(point, i) in pulsePlot.dots" :key="'point-' + i" :cx="point.x" :cy="point.y" r="1.5" fill="currentColor" />
          </svg>
          <div class="health-link__chart-times"><span>{{ time(linked.startedAt) }}</span><span>{{ time(linked.endedAt) }}</span></div>
        </div>
        <p v-if="linked.heartRateStatus !== 'samples'" class="health-link__note">
          {{ linked.heartRateStatus === 'limited' ? 'Die Quelle liefert einzelne Pulswerte, aber keinen vollständigen Verlauf.' : 'Für diese Aufzeichnung sind keine zugehörigen Pulswerte verfügbar.' }}
        </p>
        <p v-if="linked.metricsAssociation === 'source_time_window'" class="health-link__note">Messwerte derselben Quelle im Zeitraum der Aufzeichnung. Sie sind keine laufende Pulsmessung.</p>
      </template>
      <template v-if="linked.provider === 'health_connect' && !pending">
        <p class="health-link__note" role="status">{{ deviceLoading ? 'Messwerte werden aus Health Connect geladen …' : deviceError || (hasMeasurements ? 'Messwerte direkt von diesem Handy. Im Konto ist nur die Zuordnung gespeichert.' : 'Mit Health Connect verknüpft. Öffne dieses Training auf deinem Android-Handy und gib die Aufzeichnung frei, um Messwerte zu sehen.') }}</p>
        <button v-if="isAndroid && ownExecution && available" type="button" class="health-link__reload" :disabled="deviceLoading" @click="checkAvailability">Messwerte aktualisieren</button>
      </template>
      <p v-if="pending" class="health-link__pending"><v-icon small color="primary">mdi-check</v-icon> Ausgewählt – wird mit deinem Training gespeichert</p>
      <div v-if="canManage || canUnlink" class="health-link__actions">
        <button v-if="canManage" type="button" :disabled="disabled" @click="openImport">{{ pending ? 'Auswahl ändern' : 'Auswahl / aktualisieren' }}</button>
        <button v-if="canUnlink" type="button" :disabled="disabled" @click="removeLink">{{ pending ? 'Entfernen' : 'Zuordnung lösen' }}</button>
      </div>
    </template>
    <template v-else>
      <p class="health-link__description">Ergänze dein Training mit einer vorhandenen Aufzeichnung aus {{ importLabel }}.</p>
      <v-btn block rounded depressed color="primary" class="health-link__cta" :disabled="disabled" @click="openImport">
        <v-icon left size="19">mdi-plus</v-icon> Aus {{ importLabel }} hinzufügen
      </v-btn>
      <p class="health-link__note">Auch später im Trainingshub möglich.</p>
    </template>

    <v-dialog v-model="dialog" max-width="520" :persistent="stage === 'saving'" scrollable dark
      content-class="health-import-dialog" style="z-index: 100100" transition="dialog-bottom-transition">
      <v-card class="health-import-sheet">
        <header class="health-import-sheet__header">
          <div><span class="health-link__eyebrow">{{ importLabel }}</span><h2>{{ stage === 'unlink' ? 'Zuordnung lösen?' : 'Verbinde dein Training' }}</h2></div>
          <v-btn icon :disabled="stage === 'saving'" aria-label="Gesundheitsdaten-Import schliessen" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="health-import-sheet__body">
          <div class="health-import-target"><v-icon color="primary" size="21">mdi-dumbbell</v-icon><div><strong>{{ execution.title || 'Dein Training' }}</strong><span>{{ referenceLabel }}</span></div></div>

          <template v-if="stage === 'intro'">
            <p class="health-import-lead">Ergänze deine Sätze und Gewichte mit den verfügbaren Messwerten deiner Health-Aufzeichnung.</p>
            <ol class="health-import-steps">
              <li><span>1</span><div><strong>Gespeicherte Aufzeichnung verwenden</strong><p>Wähle die abgeschlossene Aufzeichnung, die zu deinem App-Training gehört.</p></div></li>
              <li><span>2</span><div><strong>Mit {{ importLabel }} synchronisieren</strong><p>Öffne bei Bedarf deine Health-App. Die Freigabe für {{ importLabel }} muss dort aktiviert sein.</p></div></li>
              <li><span>3</span><div><strong>Passendes Training auswählen</strong><p>Wir suchen nach Aufzeichnungen im passenden Zeitraum. Du bestätigst die Zuordnung.</p></div></li>
            </ol>
            <p class="health-import-privacy"><v-icon small color="#a3aea8">mdi-lock-outline</v-icon> {{ isAndroid ? 'Im Konto speichern wir nur die Zuordnung. Messwerte werden bei Bedarf aus Health Connect auf diesem Handy geladen.' : 'Nur die von dir bestätigte Aufzeichnung wird mit deinem Training im well come FIT Konto gespeichert.' }}</p>
          </template>

          <div v-if="busy" class="health-import-loading" role="status" aria-live="polite">
            <v-progress-circular indeterminate color="primary" :size="42" :width="3" />
            <strong>{{ loadingLabel }}</strong>
            <span>{{ stage === 'authorizing' ? 'Bitte prüfe die Freigabe auf deinem Handy.' : 'Deine Trainingsdaten bleiben erhalten.' }}</span>
          </div>

          <template v-if="stage === 'results' || stage === 'error'">
            <div class="health-import-date">
              <label for="health-import-search-day">Aufzeichnungen vom</label>
              <input id="health-import-search-day" v-model="searchDate" type="date" :max="today" @change="search(false)" />
              <v-btn icon aria-label="Aufzeichnungen erneut suchen" @click="search(false)"><v-icon>mdi-refresh</v-icon></v-btn>
            </div>
            <p v-if="!reference.exact" class="health-link__note">Bei älteren Trainings kann die Uhrzeit abweichen. Prüfe Datum und Dauer oder wähle einen anderen Tag.</p>
            <div v-if="error" class="health-import-error" role="alert">{{ error }}</div>
            <div v-if="!results.length && !error" class="health-import-empty" role="status">
              <v-icon color="#b8c3bc" size="38">mdi-watch-export-variant</v-icon>
              <h3>Noch keine Aufzeichnung gefunden</h3>
              <p>Öffne deine Health-App zur Synchronisierung. Sobald die Aufzeichnung in {{ importLabel }} angekommen und freigegeben ist, können wir sie hier finden.</p>
              <p v-if="isAndroid">Prüfe in Health Connect die Freigabe für Trainings und den ausgewählten Tag. Diese erste Version durchsucht die letzten 29 Tage.</p>
              <template v-else>
                <p>Prüfe bei Bedarf in Apple Health, ob das Training vorhanden ist und well come FIT es lesen darf. Apple teilt uns nicht mit, ob ein Lesezugriff abgelehnt wurde.</p>
                <details><summary>Die Daten fehlen weiterhin?</summary><p>In Apple Health unter Profil → Apps und Dienste → well come FIT die gewünschten Lesefreigaben prüfen. Auch die Uhren-App muss Trainings an Apple Health weitergeben. Garmin übernimmt beim erstmaligen Aktivieren nur bis zu zwei Wochen rückwirkend.</p></details>
              </template>
            </div>
            <div v-else-if="results.length" class="health-import-results" role="radiogroup" aria-label="Aufzeichnung auswählen">
              <p class="health-import-count">{{ results.length }} {{ results.length === 1 ? 'Aufzeichnung' : 'Aufzeichnungen' }} · bitte prüfen und auswählen</p>
              <button v-for="workout in results" :key="workout.workoutId" type="button" role="radio"
                :aria-checked="selectedId === workout.workoutId ? 'true' : 'false'" :disabled="isLinkedElsewhere(workout)"
                class="health-import-result" :class="{ 'is-selected': selectedId === workout.workoutId }" @click="selectedId = workout.workoutId">
                <span class="health-import-result__top"><strong>{{ workout.activityName }}</strong><span v-if="workout.strongMatch && !isLinkedElsewhere(workout)" class="health-import-match">Zeitlich passend</span></span>
                <span class="health-import-result__source">{{ sourceName(workout) }}<template v-if="workout.deviceName"> · {{ workout.deviceName }}</template></span>
                <span class="health-import-result__metrics"><span>{{ time(workout.startedAt) }} – {{ time(workout.endedAt) }}</span><strong>{{ duration(workout.durationSeconds) }}</strong><span v-if="hasNumber(workout.activeEnergyKilocalories)">{{ rounded(workout.activeEnergyKilocalories) }} kcal</span></span>
                <span v-if="isLinkedElsewhere(workout)" class="health-import-result__linked">Bereits mit „{{ workout.existingLink.title }}“ verknüpft</span>
                <span v-else-if="selectedId === workout.workoutId" class="health-import-selected"><v-icon small color="primary">mdi-check-circle</v-icon> Ausgewählt</span>
              </button>
            </div>
            <p v-if="truncated" class="health-link__note">Es werden die neuesten 200 Aufzeichnungen dieses Tages angezeigt.</p>
            <p v-if="selectedId" class="health-import-consent">{{ isAndroid ? 'Nur die Aufzeichnungs-ID wird mit diesem Training verknüpft. Puls, Kalorien und weitere Messwerte bleiben in Health Connect.' : 'Mit deiner Bestätigung werden die verfügbaren Gesundheitsdaten in deinem well come FIT Konto diesem App-Training hinzugefügt. Sätze und Gewichte bleiben erhalten.' }}</p>
          </template>
          <p v-if="stage === 'unlink'" class="health-import-lead">Die Zuordnung wird gelöst. Deine Sätze, Gewichte und die ursprüngliche Aufzeichnung bleiben erhalten. Diese Zuordnung wird nicht erneut automatisch hergestellt.</p>
        </v-card-text>
        <footer class="health-import-sheet__footer">
          <v-btn v-if="stage === 'intro'" block rounded depressed color="primary" x-large @click="search(true)">Aufzeichnungen suchen<v-icon right>mdi-arrow-right</v-icon></v-btn>
          <template v-if="stage === 'results' || stage === 'error'">
            <v-btn v-if="selectedId" block rounded depressed color="primary" x-large @click="confirmSelection">{{ pending ? 'Für dieses Training übernehmen' : 'Mit diesem Training verknüpfen' }}<v-icon right>mdi-check</v-icon></v-btn>
            <v-btn v-else block rounded depressed color="primary" x-large @click="search(false)">{{ error ? 'Erneut versuchen' : 'Erneut suchen' }}<v-icon right>mdi-refresh</v-icon></v-btn>
          </template>
          <v-btn v-if="stage === 'unlink'" block rounded depressed color="primary" x-large @click="confirmRemove">Zuordnung lösen</v-btn>
          <button v-if="!busy" type="button" class="health-import-later" @click="dialog = false">{{ linked ? 'Schliessen' : 'Später zuordnen' }}</button>
        </footer>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import appleHealth from "@/services/appleHealthImportService"
import healthConnect from "@/services/healthConnectService"
import { healthLinkPayload } from "@/services/healthImportPolicy"
import { hasDirectWatchRecording } from "@/services/watchPlatform"
import { healthWorkoutWindow, healthSearchDay, rankHealthWorkouts, formatHealthDuration, healthPulsePlot } from "@/services/healthImportMatching"
import userService from "@/services/userService"
import trainingFeatureAccess from "@/services/trainingFeatureAccess"
import api from "@/api"
import activityService from "@/services/healthActivityService"
import { healthSourceLabel } from "@/services/healthPresentation"

export default {
  name: "AppleHealthWorkoutLink",
  props: {
    execution: { type: Object, required: true },
    value: { type: Object, default: null },
    pending: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    directWatch: { type: Boolean, default: false },
  },
  data: () => ({ available: false, dialog: false, stage: "intro", results: [], selectedId: null,
    searchDate: "", error: "", truncated: false, requestVersion: 0, authorizationRequested: false, localLink: undefined,
    ownerAtOpen: "", lastSearchAt: 0, deviceWorkout: null, deviceLoading: false, deviceError: "", deviceRequest: 0, healthState: healthConnect.state, activityState: activityService.state }),
  computed: {
    featureAllowed() { return trainingFeatureAccess.isAllowed() },
    isAndroid() { return appleHealth.isAndroid() },
    importLabel() { return this.isAndroid ? "Health Connect" : "Apple Health" },
    linkedLabel() { return this.linked?.provider === "health_connect" ? "Health Connect" : "Apple Health" },
    savedLink() {
      if (!this.featureAllowed) return null
      if (this.pending) return this.value
      if (this.isAndroid && this.ownExecution && this.activityState.scope && Object.prototype.hasOwnProperty.call(this.activityState.links, this.execution._id)) return activityService.linkFor(this.execution)
      return this.localLink !== undefined ? this.localLink : this.execution.healthImport || null
    },
    linked() {
      if (this.savedLink?.provider !== "health_connect") return this.savedLink
      // Never render legacy server measurements or share another account's device readings.
      return this.ownExecution && (this.pending || this.healthState.enabled)
        ? this.pending ? this.value : this.deviceWorkout || healthLinkPayload(this.savedLink)
        : healthLinkPayload(this.savedLink)
    },
    hasMeasurements() { return Boolean(this.linked?.startedAt) },
    watchRecorded() { return this.directWatch || hasDirectWatchRecording(this.execution) },
    ownExecution() {
      const owner = this.execution.user_id?._id || this.execution.user_id
      // Reading reactive health state invalidates this computed value on account reset.
      void this.healthState.enabled
      const currentUser = String(userService.getUserId() || "")
      return Boolean(currentUser) && (!owner || String(owner) === currentUser)
    },
    canManage() { return this.featureAllowed && this.available && (!this.isAndroid || this.healthState.enabled) && this.ownExecution && !this.watchRecorded && (this.pending || Boolean(this.execution._id)) },
    canUnlink() { return this.featureAllowed && Boolean(this.linked && userService.getUserId()) && this.ownExecution && !this.watchRecorded && (this.pending || Boolean(this.execution._id)) },
    visible() { return this.featureAllowed && !this.watchRecorded && (Boolean(this.linked) || this.canManage) },
    busy() { return ["authorizing", "searching", "reading", "saving"].includes(this.stage) },
    reference() { return healthWorkoutWindow(this.execution) },
    referenceLabel() { return `${this.dateTime(this.reference.to)} · ${formatHealthDuration(this.execution.duration)}` },
    today() { return this.$moment().format("YYYY-MM-DD") },
    pulsePlot() { return healthPulsePlot(this.linked) },
    loadingLabel() {
      return { authorizing: `${this.importLabel} prüfen`, searching: "Passende Aufzeichnungen suchen", reading: "Gesundheitsdaten laden", saving: "Zuordnung speichern" }[this.stage]
    },
  },
  watch: {
    featureAllowed(allowed) {
      if (allowed) { this.checkAvailability(); return }
      this.requestVersion++; this.dialog = false; this.results = []; this.selectedId = null
      this.localLink = undefined; this.available = false; this.error = ""
      this.clearDeviceWorkout()
      this.$emit("available", false)
      if (this.pending) this.$emit("input", null)
    },
    dialog(open) { if (!open) { this.requestVersion++; this.results = []; this.selectedId = null; this.error = "" } },
    "execution._id"() { this.localLink = undefined; this.dialog = false },
    "execution.healthImport"() { this.localLink = undefined },
    savedLink() { this.clearDeviceWorkout(); this.loadDeviceWorkout() },
    ownExecution() { this.clearDeviceWorkout(); this.loadDeviceWorkout() },
    "healthState.permissionKey"() { this.clearDeviceWorkout(); this.loadDeviceWorkout() },
    "healthState.enabled"(enabled) { this.clearDeviceWorkout(); if (enabled) this.loadDeviceWorkout() },
    watchRecorded(value) { if (value) { this.dialog = false; this.clearDeviceWorkout(); this.$emit("available", false) } },
  },
  mounted() {
    this.checkAvailability()
    window.addEventListener("healthImportBridgeReady", this.checkAvailability)
    window.addEventListener("trainingLiveForeground", this.onForeground)
    document.addEventListener("visibilitychange", this.onForeground)
  },
  beforeDestroy() {
    this.requestVersion++
    this.clearDeviceWorkout()
    window.removeEventListener("healthImportBridgeReady", this.checkAvailability)
    window.removeEventListener("trainingLiveForeground", this.onForeground)
    document.removeEventListener("visibilitychange", this.onForeground)
  },
  methods: {
    sourceName: healthSourceLabel,
    duration: formatHealthDuration,
    hasNumber(value) { return typeof value === "number" && Number.isFinite(value) },
    rounded(value) { return Math.round(value).toLocaleString("de-CH") },
    distance(value) { return value >= 1000 ? `${(value / 1000).toLocaleString("de-CH", { maximumFractionDigits: 2 })} km` : `${Math.round(value)} m` },
    time(value) { return this.$moment(value).format("HH:mm") },
    dateTime(value) { return this.$moment(value).format("DD. MMM YYYY · HH:mm") },
    async checkAvailability() {
      if (!this.featureAllowed || this.watchRecorded) { this.available = false; this.clearDeviceWorkout(); this.$emit("available", false); return }
      const access = trainingFeatureAccess.capture()
      this.available = access !== null && await appleHealth.available() && trainingFeatureAccess.isCurrent(access)
      this.$emit("available", this.canManage)
      await this.loadDeviceWorkout()
    },
    clearDeviceWorkout() { this.deviceRequest++; this.deviceWorkout = null; this.deviceLoading = false; this.deviceError = "" },
    async loadDeviceWorkout() {
      if (!this.featureAllowed || this.pending || this.watchRecorded || !this.ownExecution || !this.isAndroid ||
        !this.available || !this.healthState.enabled || this.savedLink?.provider !== "health_connect" || this.deviceLoading) return
      const owner = String(userService.getUserId() || "")
      const access = trainingFeatureAccess.capture()
      const workoutId = this.savedLink.workoutId
      const version = ++this.deviceRequest
      this.deviceWorkout = null; this.deviceLoading = true; this.deviceError = ""
      const current = () => trainingFeatureAccess.isCurrent(access) && version === this.deviceRequest && owner === String(userService.getUserId() || "") &&
        this.ownExecution && !this.watchRecorded && this.savedLink?.workoutId === workoutId
      try {
        const workout = activityService.scope() ? await activityService.read(workoutId) : await appleHealth.read(workoutId)
        if (current() && workout?.provider === "health_connect" && workout.workoutId === workoutId) this.deviceWorkout = workout
      } catch (_) {
        if (current()) this.deviceError = "Die Aufzeichnung ist auf diesem Handy nicht verfügbar oder nicht freigegeben. Die Zuordnung bleibt erhalten."
      } finally { if (version === this.deviceRequest) this.deviceLoading = false }
    },
    openImport() {
      if (!this.canManage || this.disabled) return
      this.ownerAtOpen = String(userService.getUserId() || "")
      this.accessAtOpen = trainingFeatureAccess.capture()
      this.searchDate = this.$moment(this.reference.to).format("YYYY-MM-DD")
      this.stage = "intro"; this.results = []; this.error = ""; this.selectedId = null; this.dialog = true
    },
    activeRequest(version) { return trainingFeatureAccess.isCurrent(this.accessAtOpen) && this.dialog && version === this.requestVersion && this.ownerAtOpen === String(userService.getUserId() || "") },
    onForeground() {
      this.clearDeviceWorkout()
      if (document.visibilityState === "visible") this.checkAvailability()
      if (document.visibilityState === "visible" && this.dialog && this.stage === "results" && !this.selectedId && Date.now() - this.lastSearchAt > 3000) this.search(false)
    },
    async search(requestAuthorization) {
      if (this.busy) return
      const version = ++this.requestVersion
      this.error = ""; this.selectedId = null
      const range = healthSearchDay(this.searchDate)
      if (!Number.isFinite(range.from) || this.searchDate > this.today) { this.error = "Bitte einen gültigen Trainingstag auswählen."; this.stage = "error"; return }
      try {
        if (requestAuthorization || !this.authorizationRequested) {
          this.stage = "authorizing"
          await appleHealth.authorize()
          if (!this.activeRequest(version)) return
          this.authorizationRequested = true
        }
        this.stage = "searching"; this.lastSearchAt = Date.now()
        const result = await appleHealth.search(range)
        if (!this.activeRequest(version)) return
        this.results = rankHealthWorkouts(result.workouts, this.execution)
        this.truncated = result.truncated
        this.stage = "results"
      } catch (error) {
        if (!this.activeRequest(version)) return
        this.results = []; this.error = error?.response?.data?.message || error.message || "Die Suche ist gerade nicht möglich."; this.stage = "error"
      }
    },
    isLinkedElsewhere(workout) { return Boolean(workout.existingLink && String(workout.existingLink.executionId) !== String(this.execution._id || "")) },
    async confirmSelection() {
      const selected = this.results.find((item) => item.workoutId === this.selectedId)
      if (!this.canManage || !selected || this.isLinkedElsewhere(selected) || this.busy) return
      const version = ++this.requestVersion
      this.stage = "reading"; this.error = ""
      try {
        const workout = await appleHealth.read(selected.workoutId)
        if (!this.activeRequest(version)) return
        if (this.pending) {
          this.$emit("input", workout)
        } else {
          this.stage = "saving"
          const result = await api.workoutApi.linkHealthWorkout(this.execution._id, workout)
          if (!this.activeRequest(version)) return
          this.localLink = result.healthImport
          if (this.isAndroid) activityService.noteLink(this.execution._id, result.healthImport, workout)
          this.$emit("linked", result.healthImport)
          this.$toast.success("Gesundheitsdaten mit deinem Training verknüpft")
        }
        this.dialog = false
      } catch (error) {
        if (!this.activeRequest(version)) return
        trainingFeatureAccess.reject(this.accessAtOpen, error)
        this.error = error?.response?.data?.message || error.message || "Die Zuordnung ist gerade nicht möglich."
        this.selectedId = null; this.stage = "error"
      }
    },
    removeLink() {
      if (!this.canUnlink || this.disabled) return
      this.accessAtOpen = trainingFeatureAccess.capture()
      if (this.pending) { this.$emit("input", null); return }
      this.ownerAtOpen = String(userService.getUserId() || "")
      this.stage = "unlink"; this.dialog = true
    },
    async confirmRemove() {
      if (!this.canUnlink) return
      const version = ++this.requestVersion
      this.stage = "saving"
      try {
        await api.workoutApi.unlinkHealthWorkout(this.execution._id, this.linked.workoutId)
        if (!this.activeRequest(version)) return
        this.localLink = null
        if (this.isAndroid) activityService.noteLink(this.execution._id, null)
        this.$emit("linked", null); this.dialog = false
      } catch (error) {
        if (!this.activeRequest(version)) return
        trainingFeatureAccess.reject(this.accessAtOpen, error)
        this.$toast.error(error?.response?.data?.message || "Die Zuordnung konnte nicht gelöst werden.")
        this.stage = "unlink"
      }
    },
  },
}
</script>

<style scoped>
.health-link { --health-green: var(--v-primary-base, #00963f); color: #f5faf7; background: linear-gradient(135deg, #2a2c2c, #252525 65%); border: 1px solid #a3c6c425; border-radius: 22px; padding: 22px; margin: 20px 0; text-align: left; }
.health-link--connected { border-color: #a3c6c44d; }
.health-link__head { display: flex; align-items: center; gap: 12px; }
.health-link__icon { display: grid; place-items: center; width: 44px; height: 44px; flex-shrink: 0; background: #fff; border-radius: 13px; box-shadow: 0 5px 18px #0003; }
.health-link__heading { flex: 1; min-width: 0; }
.health-link__eyebrow { display: block; font-size: 10px; font-weight: 700; letter-spacing: 1.6px; color: #a3b1a9; margin-bottom: 5px; }
.health-link h3 { color: #fff; font-size: 17px; line-height: 1.3; margin: 0; letter-spacing: -.3px; }
.health-link__description { color: #bac7bf; font-size: 14px; line-height: 1.6; margin: 17px 0; }
.health-link__cta { min-height: 46px; text-transform: none; letter-spacing: 0; font-weight: 700; }
.health-link__source { margin: 19px 0 4px; font-size: 13px; font-weight: 600; color: #d5e0d9; }
.health-link__time { font-size: 12px; color: #a0afa6; margin: 0 0 18px; }
.health-link__metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px 10px; }
.health-link__metrics div { display: flex; flex-direction: column; gap: 4px; }
.health-link__metrics strong { font-size: 21px; font-variant-numeric: tabular-nums; letter-spacing: -.5px; line-height: 1.3; }
.health-link__metrics small { font-size: 10px; letter-spacing: 0; font-weight: 500; color: #a8b7ae; }
.health-link__metrics span { font-size: 10px; color: #a8b7ae; }
.health-link__pulse { color: #f7738b; margin-top: 22px; }
.health-link__pulse > span { color: #b7c6bd; font-size: 11px; }
.health-link__pulse svg { display: block; width: 100%; height: 100px; }
.health-link__chart-times { display: flex; justify-content: space-between; color: #a8b7ae; font-size: 10px; }
.health-link__note { color: #a0aea6; font-size: 11px; line-height: 1.6; margin: 12px 0 0; }
.health-link__pending { font-size: 12px; color: #c3decf; margin: 14px 0 0; }
.health-link__actions { display: flex; justify-content: space-between; gap: 10px; border-top: 1px solid #ffffff0c; margin-top: 17px; padding-top: 5px; }
.health-link__reload { min-height: 44px; color: #82d9a6; font-size: 12px; }
.health-link__actions button { color: #b6c7bc; font-size: 12px; min-height: 44px; text-align: left; }
.health-import-sheet { background: #222 !important; border: 1px solid #ffffff14; border-radius: 26px !important; }
.health-import-sheet__header { display: flex; justify-content: space-between; align-items: center; gap: 8px; padding: 25px 23px 18px; }
.health-import-sheet h2 { font-size: 24px; font-weight: 750; line-height: 1.2; color: #fff; letter-spacing: -.7px; }
.health-import-sheet__body { padding: 0 23px 20px !important; color: #b7c5bc !important; }
.health-import-target { display: flex; align-items: center; gap: 13px; padding: 15px; border-radius: 14px; background: #ffffff05; border: 1px solid #ffffff0e; margin-bottom: 22px; }
.health-import-target strong, .health-import-target span { display: block; }
.health-import-target strong { color: #e7f0ea; font-size: 14px; line-height: 1.5; }
.health-import-target span { font-size: 11px; margin-top: 4px; color: #96ab9e; }
.health-import-lead { line-height: 1.7; font-size: 14px; }
.health-import-steps { list-style: none; margin: 23px 0; padding: 0; }
.health-import-steps li { display: flex; gap: 13px; margin-bottom: 21px; }
.health-import-steps li > span { width: 29px; height: 29px; display: grid; place-items: center; border-radius: 50%; flex-shrink: 0; background: #00963f22; color: #6cdb9d; font-weight: 700; }
.health-import-steps strong { display: block; font-size: 14px; color: #edf6f0; margin: 3px 0 5px; }
.health-import-steps p { margin: 0; font-size: 12px; line-height: 1.7; color: #aabdb1; }
.health-import-privacy { font-size: 11px; line-height: 1.7; color: #a3aea8; margin: 0 !important; }
.health-import-privacy .v-icon { margin-right: 4px; }
.health-import-sheet__footer { padding: 18px 23px max(16px, env(safe-area-inset-bottom)); border-top: 1px solid #ffffff0b; }
.health-import-sheet__footer .v-btn { text-transform: none; letter-spacing: 0; font-size: 13px; font-weight: 700; }
.health-import-later { display: block; width: 100%; min-height: 44px; font-size: 12px; color: #a0b3a7; margin-top: 5px; }
.health-import-loading { padding: 30px 8px; display: flex; align-items: center; flex-direction: column; gap: 17px; text-align: center; }
.health-import-loading strong { color: #fff; font-size: 16px; }
.health-import-loading span { font-size: 12px; }
.health-import-date { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; }
.health-import-date label { font-size: 12px; flex: 1; }
.health-import-date input { color: #fff; color-scheme: dark; font: inherit; font-size: 13px; padding: 8px; min-height: 44px; border-radius: 10px; border: 1px solid #ffffff24; max-width: 170px; }
.health-import-error { padding: 15px; border-radius: 12px; background: #f56a8017; border: 1px solid #f56a8055; color: #ffb1be; line-height: 1.6; margin-top: 14px; }
.health-import-empty { text-align: center; padding: 24px 0 4px; }
.health-import-empty h3 { font-size: 18px; color: #eef7f1; margin: 16px 0 12px; }
.health-import-empty p { font-size: 12px; line-height: 1.8; }
.health-import-empty details { text-align: left; margin-top: 20px; border-top: 1px solid #ffffff14; }
.health-import-empty summary { cursor: pointer; min-height: 44px; padding-top: 14px; color: #d3e5db; font-size: 12px; }
.health-import-count { font-size: 11px; color: #aabeb2; margin: 20px 0 12px; }
.health-import-result { display: block; width: 100%; padding: 17px; border-radius: 16px; background: #ffffff03; border: 1px solid #ffffff1b; color: #e9f0ec; text-align: left; margin-bottom: 10px; transition: border-color .16s, background .16s; }
.health-import-result.is-selected { border-color: var(--v-primary-base, #00963f); background: #00963f16; box-shadow: 0 0 0 1px var(--v-primary-base, #00963f); }
.health-import-result:disabled { opacity: .5; cursor: not-allowed; }
.health-import-result__top { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.health-import-result__top strong { font-size: 16px; }
.health-import-match { margin-left: auto; font-size: 9px; padding: 3px 6px; border-radius: 5px; color: #82d9a6; background: #00963f1c; }
.health-import-result__source { display: block; margin: 6px 0 12px; font-size: 11px; color: #9eb0a5; }
.health-import-result__metrics { display: flex; flex-wrap: wrap; gap: 9px 16px; font-size: 12px; font-variant-numeric: tabular-nums; }
.health-import-result__linked, .health-import-selected { display: block; margin-top: 12px; font-size: 11px; color: #b6d1c1; }
.health-import-consent { font-size: 11px; line-height: 1.7; margin: 17px 0 0 !important; }
button:focus-visible, input:focus-visible, summary:focus-visible { outline: 2px solid #6cdb9d; outline-offset: 4px; }
button:disabled { opacity: .55; }
@media (max-width: 380px) { .health-link { padding: 17px; } .health-link__metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); } .health-import-sheet__header { padding: 20px 17px 17px; } .health-import-sheet__body { padding: 0 17px 17px !important; } .health-import-sheet h2 { font-size: 22px; } }
@media (prefers-reduced-motion: reduce) { .health-import-result { transition: none; } }
</style>

<style>
.health-import-dialog { z-index: 100100 !important; border-radius: 26px !important; max-height: calc(100% - 32px) !important; }
</style>
